import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getAccessToken } from '../store/actions/auth';
import API_BASE_URL from '../config/baseUrl';
import { LEVEL_NAMES } from '../config/menuItems';

// Search user
const searchUser = async function(userId) {
  try {
    const token = getAccessToken();
    
    if (!token) {
      return { success: false, error: "No access token found" };
    }

    const response = await axios.post(
      API_BASE_URL + "/user/user-search",
      { userId: userId },
      { headers: { Authorization: "Bearer " + token } }
    );

    const data = response.data;

    if (data.status === false) {
      return { success: false, error: data.message || "No User Found" };
    }

    return { success: true, data: data.data };
  } catch (err) {
    const errorMessage = err.response?.data?.message || "Failed to search user";
    return { success: false, error: errorMessage };
  }
};

const UserSearchPage = () => {
  const navigate = useNavigate();

  const [searchInput, setSearchInput] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleSearch = async function() {
    if (!searchInput.trim()) {
      setError('Please enter a user ID');
      return;
    }

    setLoading(true);
    setError(null);
    setSearchResults(null);

    const result = await searchUser(searchInput.trim());

    if (result.success) {
      setSearchResults(result.data);
      setError(null);
    } else {
      setError(result.error);
      setSearchResults(null);
    }

    setLoading(false);
  };

  const handleKeyPress = function(e) {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  return (
    <div className="w-full">
      <div className="flex justify-center">
        <div className="w-full max-w-7xl px-2">
          <div className="bg-white rounded shadow border border-gray-200 mb-4">
            {/* Header */}
            <div className="bg-[#0F2327] px-5 py-3 flex items-center justify-between flex-wrap gap-2">
              <span className="text-lg sm:text-xl text-white font-normal uppercase">
                User Search
              </span>
              <button 
                onClick={function() { navigate(-1); }}
                className="bg-blue-500 text-white px-4 py-1 hover:bg-blue-600 transition-colors"
                style={{ borderRadius: 0 }}
              >
                Back
              </button>
            </div>

            {/* Search Input */}
            <div className="flex flex-wrap items-center gap-3 px-5 py-4">
              <input
                type="text"
                placeholder="Enter User ID"
                value={searchInput}
                onChange={function(e) { setSearchInput(e.target.value); }}
                onKeyPress={handleKeyPress}
                className="border border-gray-300 px-3 py-2 focus:outline-none focus:border-blue-500 w-full sm:w-auto flex-1 sm:flex-none sm:min-w-[300px]"
                style={{ borderRadius: 0 }}
              />
              <button 
                onClick={handleSearch}
                disabled={loading}
                className="bg-blue-500 text-white px-6 py-2 hover:bg-blue-600 disabled:bg-gray-400 transition-colors w-full sm:w-auto"
                style={{ borderRadius: 0 }}
              >
                {loading ? 'Searching...' : 'Show'}
              </button>
            </div>

            {/* Error Message */}
            {error && (
              <div className="mx-5 mb-4 p-3 bg-red-100 border border-red-300 text-red-700 text-sm sm:text-base">
                {error}
              </div>
            )}

            {/* Search Results Table */}
            {searchResults && searchResults.length > 0 && (
              <div className="mx-5 mb-4">
                <div className="mb-3 p-3 bg-green-100 border border-green-300 text-green-700">
                  <div className="font-semibold text-sm sm:text-base">
                    Partnership found successfully - {searchResults.length} user{searchResults.length > 1 ? 's' : ''} found
                  </div>
                </div>

                {/* Card Grid Layout */}
                <div className="flex flex-wrap justify-start mx-2 mt-4" style={{ gap: '15px' }}>
                  {searchResults.map(function(user, index) {
                    return (
                      <div 
                        key={user.userId}
                        className="w-full sm:w-[calc(50%-7.5px)] md:w-[calc(41.66%-12px)] lg:w-[calc(20%-12px)]"
                      >
                        <div 
                          className="text-white px-2 py-2 sm:py-3 flex items-center h-full"
                          style={{ 
                            backgroundImage: 'linear-gradient(-180deg, rgb(255, 140, 0), rgb(245, 76, 73) 82%)',
                            minHeight: '80px'
                          }}
                        >
                          {/* User Icon */}
                          <div className="flex-shrink-0">
                            <svg 
                              stroke="currentColor" 
                              fill="none" 
                              strokeWidth="2" 
                              viewBox="0 0 24 24" 
                              aria-hidden="true" 
                              className="text-white w-10 h-10 sm:w-12 sm:h-12 md:w-14 md:h-14 lg:w-16 lg:h-16"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path 
                                strokeLinecap="round" 
                                strokeLinejoin="round" 
                                d="M5.121 17.804A13.937 13.937 0 0112 16c2.5 0 4.847.655 6.879 1.804M15 10a3 3 0 11-6 0 3 3 0 016 0zm6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                              />
                            </svg>
                          </div>
                          
                          {/* User Info */}
                          <div className="px-2 flex-1 min-w-0 overflow-hidden">
                            <div className="text-sm sm:text-base md:text-lg lg:text-xl font-extrabold break-words overflow-wrap-anywhere">
                              {user.userName} ({user.userId})
                            </div>
                            <div className="text-xs sm:text-sm md:text-base lg:text-lg font-normal mt-1">
                              {LEVEL_NAMES[user.userType] || 'Unknown'}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Summary Info */}
                <div className="mt-3 text-xs sm:text-sm text-gray-600">
                  Showing {searchResults.length} result{searchResults.length > 1 ? 's' : ''}
                </div>
              </div>
            )}

            {/* No Results */}
            {searchResults && searchResults.length === 0 && (
              <div className="mx-5 mb-4 p-4 text-center text-gray-500">
                <svg 
                  className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-3 text-gray-300" 
                  viewBox="0 0 64 41"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <g transform="translate(0 1)" fillRule="evenodd">
                    <ellipse className="fill-current opacity-25" cx="32" cy="33" rx="32" ry="7" />
                    <g className="fill-current opacity-25" fillRule="nonzero">
                      <path d="M55 12.76L44.854 1.258C44.367.474 43.656 0 42.907 0H21.093c-.749 0-1.46.474-1.947 1.257L9 12.761V22h46v-9.24z" />
                      <path d="M41.613 15.931c0-1.605.994-2.93 2.227-2.931H55v18.137C55 33.26 53.68 35 52.05 35h-40.1C10.32 35 9 33.259 9 31.137V13h11.16c1.233 0 2.227 1.323 2.227 2.928v.022c0 1.605 1.005 2.901 2.237 2.901h14.752c1.232 0 2.237-1.308 2.237-2.913v-.007z" />
                    </g>
                  </g>
                </svg>
                <p className="text-sm sm:text-base">No users found</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserSearchPage;